import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SpotlightSearch } from './SpotlightSearch';

interface SpotlightContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

const SpotlightContext = createContext<SpotlightContextValue | null>(null);

export function useSpotlightContext() {
  const context = useContext(SpotlightContext);
  if (!context) {
    throw new Error('useSpotlightContext must be used within a SpotlightProvider');
  }
  return context;
}

// Detect platform from URL
function detectPlatform(pathname: string): 'admin' | 'teacher' | 'student' | 'super-admin' {
  if (pathname.startsWith('/super-admin')) return 'super-admin';
  if (pathname.startsWith('/teacher')) return 'teacher';
  if (pathname.startsWith('/student')) return 'student';
  return 'admin';
}

export function SpotlightProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const platform = detectPlatform(location.pathname);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  // Global keyboard shortcut (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        toggle();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [toggle]);

  // Close spotlight on route change
  useEffect(() => {
    close();
  }, [location.pathname, close]);

  return (
    <SpotlightContext.Provider value={{ isOpen, open, close, toggle }}>
      {children}
      <SpotlightSearch isOpen={isOpen} onClose={close} platform={platform} />
    </SpotlightContext.Provider>
  );
}

export default SpotlightProvider;
